import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import OpenIcon from '@mui/icons-material/OpenInNew'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

interface IndividualAnalysis {
  content: string
  generatedAt: string
  section: string
  type: string
  job?: string // Optional job for cross-job multi-table support
}

interface IndividualAnalysesModalProps {
  open: boolean
  onClose: () => void
  sectionTitle: string
  tablesUsed: string[]
  individualAnalyses: Record<string, IndividualAnalysis>
  tableJobs?: Record<string, string> // Optional map of table name -> job name for cross-job support
  onOpenTable: (tableName: string, job?: string) => void
}

export const IndividualAnalysesModal: React.FC<IndividualAnalysesModalProps> = ({
  open,
  onClose,
  sectionTitle,
  tablesUsed,
  individualAnalyses,
  tableJobs,
  onOpenTable
}) => {
  const [selectedTable, setSelectedTable] = useState<string | null>(null)
  const [selectedAnalysis, setSelectedAnalysis] = useState<IndividualAnalysis | null>(null)

  // Reset selection when modal opens/closes
  React.useEffect(() => {
    if (open && tablesUsed.length > 0 && !selectedTable) {
      const firstTable = tablesUsed[0]
      setSelectedTable(firstTable)
      setSelectedAnalysis(individualAnalyses[firstTable] || null)
    } else if (!open) {
      setSelectedTable(null)
      setSelectedAnalysis(null)
    }
  }, [open, tablesUsed, individualAnalyses, selectedTable])

  const handleTableSelect = (tableName: string) => {
    setSelectedTable(tableName)
    setSelectedAnalysis(individualAnalyses[tableName] || null)
  }

  // Helper to get job for a table (from individualAnalysis or tableJobs map)
  const getTableJob = (tableName: string): string | undefined => {
    return individualAnalyses[tableName]?.job || tableJobs?.[tableName]
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          height: '90vh',
          maxHeight: '900px',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Individual Table Analyses</Typography>
            <Typography variant="subtitle2" color="text.secondary">
              {sectionTitle} - {tablesUsed.length} tables analyzed
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 0, display: 'flex', height: '100%' }}>
        {/* Left sidebar with table list */}
        <Box sx={{ 
          width: 300, 
          borderRight: 1, 
          borderColor: 'divider',
          overflow: 'auto',
          flexShrink: 0
        }}>
          <List sx={{ p: 0 }}>
            {tablesUsed.map((tableName, index) => (
              <ListItem key={tableName} disablePadding>
                <ListItemButton
                  selected={selectedTable === tableName}
                  onClick={() => handleTableSelect(tableName)}
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                      '& .MuiListItemText-primary': {
                        color: 'white',
                      },
                      '& .MuiListItemText-secondary': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                      '& .MuiSvgIcon-root': {
                        color: 'white',
                      },
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        {getTableJob(tableName) && (
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{
                              bgcolor: selectedTable === tableName ? 'rgba(255,255,255,0.3)' : 'info.main',
                              color: selectedTable === tableName ? 'white' : 'info.contrastText',
                              px: 0.5,
                              py: 0.25,
                              borderRadius: 0.5,
                              fontSize: '0.65rem',
                            }}
                          >
                            {getTableJob(tableName)}
                          </Typography>
                        )}
                        <span>{tableName}</span>
                      </Box>
                    }
                    secondary={individualAnalyses[tableName] ? 'Analysis available' : 'No analysis'}
                    primaryTypographyProps={{
                      fontSize: '0.9rem',
                      fontWeight: selectedTable === tableName ? 600 : 400,
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      onOpenTable(tableName, getTableJob(tableName))
                    }}
                    sx={{ ml: 1 }}
                  >
                    <OpenIcon fontSize="small" />
                  </IconButton>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Right content area */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          {selectedAnalysis ? (
            <Paper elevation={0} sx={{ p: 3, backgroundColor: 'grey.50' }}>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      {selectedTable && getTableJob(selectedTable) && (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{
                            bgcolor: 'info.main',
                            color: 'info.contrastText',
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 0.5,
                            fontSize: '0.7rem',
                          }}
                        >
                          {getTableJob(selectedTable)}
                        </Typography>
                      )}
                      <Typography variant="h6">
                        {selectedTable}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Generated: {new Date(selectedAnalysis.generatedAt).toLocaleString()}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => selectedTable && onOpenTable(selectedTable, getTableJob(selectedTable))}
                    sx={{ 
                      ml: 1,
                      opacity: 0.7,
                      '&:hover': { opacity: 1 }
                    }}
                    title="Open table in modal"
                  >
                    <OpenIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              
              <Divider sx={{ mb: 3 }} />
              
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h2: ({ children }) => (
                    <Typography variant="h6" sx={{ mt: 3, mb: 1.5, fontWeight: 600 }}>
                      {children}
                    </Typography>
                  ),
                  h3: ({ children }) => (
                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                      {children}
                    </Typography>
                  ),
                  p: ({ children }) => (
                    <Typography variant="body2" paragraph sx={{ mb: 1.5, lineHeight: 1.7 }}>
                      {children}
                    </Typography>
                  ),
                  ul: ({ children }) => (
                    <Box component="ul" sx={{ pl: 3, mb: 1.5, mt: 0 }}>
                      {children}
                    </Box>
                  ),
                  li: ({ children }) => (
                    <Typography component="li" variant="body2" sx={{ mb: 0.5, lineHeight: 1.7 }}>
                      {children}
                    </Typography>
                  ),
                  strong: ({ children }) => (
                    <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                      {children}
                    </Typography>
                  ),
                }}
              >
                {selectedAnalysis.content}
              </ReactMarkdown>
            </Paper>
          ) : selectedTable ? (
            <Alert severity="info">
              No individual analysis available for table: {selectedTable}
            </Alert>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%' 
            }}>
              <Typography variant="body1" color="text.secondary">
                Select a table from the list to view its analysis
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}